import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Plus, User, LogOut, LayoutDashboard, ShieldCheck, Ticket, Coins, Home as HomeIcon, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV = [
  { label: "Properties", to: "/listings/properties" },
  { label: "Vehicles", to: "/listings/vehicles" },
  { label: "Healthcare", to: "/listings/healthcare" },
  { label: "Education", to: "/listings/education" },
  { label: "Food", to: "/listings/food" },
  { label: "Businesses", to: "/listings/businesses" },
  { label: "Master Plan", to: "/master-plan" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  const initials = user && user.name ? user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "U";
  const isAdmin = user && user.role === "admin";
  const isSeller = user && (user.role === "seller" || user.role === "admin");

  const doLogout = async () => { await logout(); navigate("/"); };

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setShowSearch(false);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  // Close mobile search on outside click / escape
  useEffect(() => {
    if (!showSearch) return;
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setShowSearch(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showSearch]);

  return (
    <header className="sticky top-0 z-[60] bg-white/95 backdrop-blur border-b border-border" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="logo-link">
            <img src="/logo.jpeg" alt="The Pataudi" className="h-10 w-10 sm:h-11 sm:w-11 rounded-full object-cover ring-1 ring-black/10 bg-black shrink-0" loading="eager" />
            <div className="leading-none min-w-0">
              <span className="font-display font-bold text-base sm:text-lg text-ink block truncate">The Pataudi</span>
              <span className="text-[10px] sm:text-[11px] text-muted-foreground -mt-0.5 block truncate">Find. Connect. Done.</span>
            </div>
          </Link>

          {/* Global search bar (desktop) */}
          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-sm relative" data-testid="header-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search listings by title, category, keywords…"
              className="w-full pl-9 pr-4 py-2 rounded-full border border-border bg-surface/60 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-white transition"
              data-testid="header-search-input"
            />
          </form>

          {user && !isAdmin && (
            <button
              onClick={() => navigate("/pricing")}
              title={(user.credits ?? 0) > 0 ? `${user.credits} credit${user.credits === 1 ? "" : "s"} left — click to buy more` : "No credits left — buy a plan"}
              className={`hidden lg:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                (user.credits ?? 0) > 0
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
              }`}
              data-testid="credits-badge"
            >
              <Coins className="h-3.5 w-3.5" />
              Credits: <span className="tabular-nums" data-testid="credits-count">{user.credits ?? 0}</span>
            </button>
          )}
          {user && isAdmin && (
            <span
              title="Admin has full access — all listings and contacts are free."
              className="hidden lg:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border bg-primary/10 border-primary/30 text-primary"
              data-testid="admin-access-badge"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Admin · Full Access
            </span>
          )}

          <nav className="hidden xl:flex items-center gap-0.5" data-testid="primary-nav">
            <Link to="/" className="px-2 py-2 text-sm font-medium text-ink/80 hover:text-primary rounded-md transition-colors duration-200 flex items-center gap-1" data-testid="nav-home" title="Home">
              <HomeIcon className="h-4 w-4" />
            </Link>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="px-2 py-2 text-sm font-medium text-ink/80 hover:text-primary rounded-md transition-colors duration-200" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            {/* Home icon (visible on md/lg where nav is hidden) */}
            <Link to="/" title="Home" className="xl:hidden p-2 rounded-md hover:bg-ink/5 text-ink/80" data-testid="nav-home-icon">
              <HomeIcon className="h-5 w-5" />
            </Link>

            {(!user || isSeller) && (
              <Button onClick={() => navigate(user ? "/dashboard/create-listing" : "/become-seller")} className="bg-accent hover:bg-accent/90 text-white rounded-full gap-1.5 px-4 h-10 shadow-pop hover:shadow-lg transition-shadow whitespace-nowrap" data-testid="post-listing-btn">
                <Plus className="h-4 w-4 shrink-0" /> <span className="hidden lg:inline">Post Free</span><span className="lg:hidden">Post</span>
              </Button>
            )}
            {user && !isSeller && !isAdmin && (
              <Button onClick={() => navigate("/become-seller")} variant="outline" className="rounded-full gap-1.5 h-10 px-4 border-primary/30 text-primary hover:bg-primary/5 whitespace-nowrap" data-testid="start-selling-btn">
                <Plus className="h-4 w-4 shrink-0" /> Start Selling
              </Button>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2" data-testid="user-menu-btn">
                    {isAdmin && (
                      <span className="hidden xl:inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full">
                        <ShieldCheck className="h-3.5 w-3.5" /> Admin
                      </span>
                    )}
                    <span className="h-9 w-9 rounded-full bg-primary text-white font-medium text-sm flex items-center justify-center">{initials}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="w-56 z-[80]">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium text-ink truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="text-primary font-medium">
                      <ShieldCheck className="h-4 w-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  {!isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-buy-credits" className="text-accent font-medium">
                      <Ticket className="h-4 w-4 mr-2" /> Buy Plan
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" /> Profile</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full px-5 border-primary/30 text-primary hover:bg-primary/5" data-testid="login-btn">Login</Button>
            )}
          </div>

          {/* Mobile + tablet actions (hamburger visible whenever primary nav is hidden) */}
          <div className="xl:hidden flex items-center gap-1 ml-auto md:ml-2">
            {/* Compact "Post Free" pill for mobile — visible even before login */}
            <Link
              to={user ? "/dashboard/create-listing" : "/become-seller"}
              className="md:hidden inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-accent text-white text-[11px] font-semibold shadow-pop hover:bg-accent/90 transition-colors whitespace-nowrap"
              data-testid="mobile-post-free-btn"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" /> Post Free
            </Link>
            <button className="p-2 rounded-md hover:bg-ink/5 md:hidden" onClick={() => setShowSearch((s) => !s)} data-testid="mobile-search-toggle" title="Search">
              <Search className="h-5 w-5" />
            </button>
            <Link to="/" className="p-2 rounded-md hover:bg-ink/5 md:hidden" data-testid="mobile-home-btn" title="Home">
              <HomeIcon className="h-5 w-5" />
            </Link>
            <button className="p-2 rounded-md hover:bg-ink/5 border border-transparent hover:border-border transition-colors" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Open menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile search overlay */}
        {showSearch && (
          <div ref={searchRef} className="md:hidden pb-3" data-testid="mobile-search-bar">
            <form onSubmit={submitSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title, category, keywords…"
                className="w-full pl-9 pr-4 py-2 rounded-full border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="mobile-search-input"
              />
            </form>
          </div>
        )}
      </div>

      {open && (
        <div className="xl:hidden border-t border-border bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ink rounded-md hover:bg-surface"><HomeIcon className="h-4 w-4" /> Home</Link>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 text-sm font-medium text-ink rounded-md hover:bg-surface">{n.label}</Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {(!user || isSeller) && (
              <Button onClick={() => { setOpen(false); navigate(user ? "/dashboard/create-listing" : "/become-seller"); }} className="bg-accent hover:bg-accent/90 text-white rounded-lg gap-1.5"><Plus className="h-4 w-4" /> Post Free Listing</Button>
            )}
            {user && !isSeller && !isAdmin && (
              <Button onClick={() => { setOpen(false); navigate("/become-seller"); }} variant="outline" className="rounded-lg gap-1.5 border-primary/30 text-primary"><Plus className="h-4 w-4" /> Start Selling</Button>
            )}
            {user ? (
              <>
                {!isAdmin && (
                  <Button variant="outline" className="rounded-lg gap-1.5 border-accent/40 text-accent" onClick={() => { setOpen(false); navigate("/pricing"); }} data-testid="mobile-buy-credits"><Ticket className="h-4 w-4" /> Buy Plan ({user.credits ?? 0} credits)</Button>
                )}
                {isAdmin && (
                  <Button variant="outline" className="rounded-lg gap-1.5 border-primary/40 text-primary" onClick={() => { setOpen(false); navigate("/admin"); }} data-testid="mobile-admin">
                    <ShieldCheck className="h-4 w-4" /> Admin Panel
                  </Button>
                )}
                <Button variant="outline" className="rounded-lg" onClick={() => { setOpen(false); navigate("/dashboard"); }}>Dashboard</Button>
                <Button variant="ghost" className="rounded-lg text-destructive" onClick={() => { setOpen(false); doLogout(); }}>Logout</Button>
              </>
            ) : (
              <Button variant="outline" className="rounded-lg" onClick={() => { setOpen(false); navigate("/login"); }}>Login</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
